<template>
  <div
    ref="elementRef"
    v-style="{
      ...brandStyles[isFullHeight && !hasComponentPercentHeight
        ? 'containerFullHeight' : 'container']?.(headerHeight, promoBarHeight),
      bg: backgroundColor,
    }"
    class="relative"
    :class="[
      roundedCornersClasses,
      {
        'overflow-hidden': parallaxLayer,
        'children:h-inherit overflow-hidden':
          usesPercentHeightSmMd && alignmentSmall !== 'below',
        '<md:flex <md:flex-col':
          usesPercentHeightSmMd && alignmentSmall === 'below',
        'lg:overflow-hidden': alignmentLarge !== 'below',
        'lg:flex lg:flex-col': alignmentLarge === 'below',
        'children:h-inherit': !hasComponentPercentHeight && isFullHeight,
        'o:lg:h-$h-lg': usesPercentHeightLg,
        'o:h-$h': usesPercentHeightSmMd && usesPercentHeightLg,
        'o:<lg:h-$h': usesPercentHeightSmMd && !usesPercentHeightLg,
        '<md:grid': alignmentSmall !== 'below',
        'md:grid': alignmentLarge !== 'below',
      },
    ]"
    :style="percentHeightStyle"
  >
    <div
      v-if="parallaxOffset"
      v-style="{ pb: parallaxPaddingBottom }"
      class="relative grid-area-stack"
      :class="[
        {
          '<lg:h-full <lg:overflow-hidden': usesPercentHeightSmMd,
          'lg:h-full lg:overflow-hidden': usesPercentHeightLg,
        },
      ]"
    >
      <div ref="gradientAreaRef" class="absolute-0 flex items-center overflow-hidden">
        <div ref="parallaxRef" class="w-full" :style="{ height: `calc(100% + ${parallaxTranslate})` }">
          <base-picture
            v-if="responsiveMedia.images"
            v-style:max-h="cinemaModeMaxH"
            v-bind="responsiveMedia.images"
            class="full"
            :fetchpriority
            fit="cover"
            :lazy
            :position="objectPosition"
            :props-img="imageProps"
          />
          <vf-video
            v-else-if="responsiveMedia.video"
            v-bind="responsiveMedia.video"
            ref="videoRef"
            v-style:max-h="cinemaModeMaxH"
            :autoplay="!lazy"
            class="full"
            :controls
            :directives="{ 'v-style': { objectPosition } }"
            fit="cover"
            loop
            :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
          />
          <cms-shared-hot-zones v-if="hasHotZones" :media />
        </div>
        <cms-base-gradient
          v-if="gradientStyle && gradientStyle !== 'off'"
          v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
          :alignment="gradientAlignment"
          :position="gradientPosition"
          :text-area-only-extent
        />
        <base-link
          v-if="mediaLink"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
        <cms-base-caption
          v-if="hasCaption"
          v-bind="captionProps"
          :align-with-overlay
          :class="captionClasses"
        />
      </div>
    </div>

    <div
      v-else
      ref="gradientAreaRef"
      class="relative grid-area-stack"
      :class="{
        '<lg:h-full <lg:overflow-hidden': usesPercentHeightSmMd,
        'lg:h-full lg:overflow-hidden': usesPercentHeightLg,
      }"
    >
      <base-picture
        v-if="responsiveMedia.images"
        v-style:max-h="cinemaModeMaxH"
        v-bind="responsiveMedia.images"
        class="full"
        :fetchpriority
        fit="cover"
        :lazy
        :position="objectPosition"
        :props-img="imageProps"
      />
      <vf-video
        v-else-if="responsiveMedia.video"
        v-bind="responsiveMedia.video"
        ref="videoRef"
        v-style:max-h="cinemaModeMaxH"
        :autoplay="!lazy"
        class="full"
        :controls
        :directives="{ 'v-style': { objectPosition } }"
        fit="cover"
        loop
        :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
      />
      <cms-shared-hot-zones v-if="hasHotZones" :media />
      <cms-base-gradient
        v-if="gradientStyle && gradientStyle !== 'off'"
        v-bind="{ gradientTone, gradientStyle, gradientGrid, gradientBreakpoints }"
        :alignment="gradientAlignment"
        :position="gradientPosition"
        :text-area-only-extent
      />
      <base-link
        v-if="mediaLink && !hasHotZones"
        aria-hidden="true"
        class="absolute-0"
        :data-cta="mediaLink.delocalizedTracking"
        tabindex="-1"
        :target="mediaLink.targetAttribute"
        :to="mediaLink.url"
        @click="handlePromotionClick('img')"
      />
      <cms-base-caption
        v-if="hasCaption"
        v-bind="captionProps"
        :align-with-overlay
        :class="captionClasses"
      />
    </div>
    <cms-base-parallax-layer
      class="pointer-events-none z-1 flex"
      :class="[
        contentAlignmentSmall[alignmentSmall],
        contentAlignmentLarge[alignmentLarge],
        textsAlignmentSmall[textAlignmentSmall],
        textsAlignmentLarge[textAlignmentLarge],
        brandClasses!.containerWrapper,
        containerWrapperExtendedClasses,
        {
          'items-center': !!contentAlignmentLargeOld[alignmentLarge],
          'md:o:h-a': usesPercentHeightLg && alignmentLarge === 'below',
          '<md:o:h-a': usesPercentHeightSmMd && alignmentSmall === 'below',
          '<md:grid-area-stack': alignmentSmall !== 'below',
          'md:grid-area-stack': alignmentLarge !== 'below',
        },
      ]"
      :config="parallaxLayer"
    >
      <div
        ref="textAreaRef"
        v-style="{
          bg: backdrop,
          pb: { sm: firstElement && floatingNavigation && usesPercentHeightSmMd ? '5rem' : 0, lg: 0 },
        }"
        class="relative f-col overflow-hidden"
        :class="[
          overlayWidth ? contentSize[overlayWidth] : brandClasses!.containerWidth,
          brandClasses!.container,
          blurClass,
          overlayRoundedCornerClasses,
        ]"
      >
        <cms-base-eyebrow
          v-if="eyebrowText || eyebrowTextHtml || eyebrowLogoResponsive"
          :class="brandClasses!.eyebrow"
          :eyebrow-logo-responsive
          :eyebrow-logo-width-responsive
          :eyebrow-text
          :eyebrow-text-color
          :eyebrow-text-html
          :eyebrow-text-style
          :position="{ sm: textAlignmentSmall, md: textAlignmentLarge }"
        />
        <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
          {{ overline }}
        </p>
        <div
          v-if="title || subtitle || richText || targets.length"
          :class="(title && !hideTitle) || subtitle || richText || targets.length ? ['f-col', brandClasses!.textContainerOuter] : 'contents'"
        >
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <cms-plain-text :content field-name="title" />
            </base-link>
            <template v-else>
              <cms-plain-text :content field-name="title" />
            </template>
          </component>
          <div v-if="subtitle || richText" class="f-col" :class="brandClasses!.textContainerInner">
            <p
              v-if="subtitle"
              v-style:c="subtitleColor"
              :class="subtitleStyle"
              data-test-id="cms-banner-text"
            >
              <cms-plain-text :content field-name="subtitle" />
            </p>
            <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" class="pointer-within" :content="{ richText }" />
          </div>
          <cms-shared-cta-group
            v-if="linkType !== 'No-CTA' && targets.length"
            class="pointer-within md:items-center"
            :class="[
              ctaAlignmentSmall[alignmentSmall],
              ctaAlignmentSmall[textAlignmentSmall],
              ctaAlignmentLarge[alignmentLarge],
              ctaAlignmentLarge[textAlignmentLarge],
            ]"
            :cols
            :cta-type
            :equal-size-small-class="brandClasses!.ctaEqualSizeSmall"
            :equal-target-size
            :layout="brandClasses!.ctaContainer"
            :multilink-style
            :multilink-title
            :size
            :targets
            :text-link-style="getTextLinkStyles(content)"
            @click="handlePromotionClick(`cta${$event + 1}`)"
          />
          <cms-rich-text
            v-if="richTextBelowTargets"
            v-style:c="richTextBelowTargetsColor || subtitleColor"
            class="pointer-within"
            :content="{ richText: richTextBelowTargets }"
          />
        </div>
      </div>
    </cms-base-parallax-layer>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { Responsive } from '#types/common'
import type {
  BannerImageAlignmentLarge,
  BannerImageAlignmentSmall,
  BannerImageContent
} from '#types/components/cms/banner-image'
import type { TextAlignment } from '#types/components/cms/text-alignment'
import type { Content } from '#types/content'
import type { Target } from '#types/target'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const {
  content,
  controls = true,
  firstElement,
  index,
  isActiveSlide,
  isPartOfCollection,
  parentHeadingLevel,
  paused
} = defineProps<{
  content: BannerImageContent
  controls?: boolean
  firstElement?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
  paused?: boolean
  /**
   * Whether this slide is currently active in a carousel.
   * When undefined (not in carousel), the impression fires normally.
   * When in carousel, the impression only fires when isActiveSlide is true.
   */
  isActiveSlide?: boolean
}>()

const {
  subtitleColor,
  alignmentLarge,
  alignmentSmall,
  backdrop,
  backdropBlur,
  backgroundColor,
  alignWithOverlay,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor = subtitleColor,
  eyebrowTextStyle,
  gradientBreakpoints,
  gradientGrid,
  gradientStyle,
  gradientTone,
  hideTitle,
  isFullHeight,
  linkType,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  overlayWidth,
  parallax,
  cinemaMode,
  overline,
  overlineColor,
  overlineStyle,
  promotionTracking,
  richText,
  richTextBelowTargets,
  richTextBelowTargetsColor,
  richTextColor,
  subtitle,
  subtitleStyle,
  targets = [] as Target[],
  textAlignmentLarge,
  textAlignmentSmall,
  title,
  titleColor,
  titleH1,
  titleStyle,
  componentPercentHeight
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const appConfig = useAppConfig()
const cms = appConfig.components.cms
const { enableTextAreaOnlyGradient } = cms.gradient
const { size } = cms.sharedButton
const { brandClasses, brandStyles } = cms.imageBanner
const { componentCorners } = cms.section
const { floatingNavigation, promoBarHeight } = appConfig.components.vf.header
const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const header = useHeaderStore()
const { elementRef, handlePromotionClick, isVisible } = usePromotionTracking(promotionTracking, content.name)
const { $gtm } = useNuxtApp()
const isTextAreaOnlyGradient = isTextAreaOnlyGradientEnabled(enableTextAreaOnlyGradient, gradientStyle)

// Hero Banner CTA Impression tracking - fires once per session per banner
const heroBannerSessionKey = `hero-banner-impression-${content.name}`
const hasTrackedImpression = ref(false)

const stopWatch = watch(
  [isVisible, () => isActiveSlide],
  ([visible]) => {
    if (visible && isClient() && !hasTrackedImpression.value && isActiveSlide !== false) {
      if (!sessionStorage.getItem(heroBannerSessionKey)) {
        sessionStorage.setItem(heroBannerSessionKey, 'true')
        $gtm.push('hero.onImpression', content.name || '')
        hasTrackedImpression.value = true
        stopWatch() // Stop watching after successful tracking
      }
    }
  },
  { immediate: true }
)

const videoRef = ref()
const gradientAreaRef = useTemplateRef('gradientAreaRef')
const textAreaRef = useTemplateRef('textAreaRef')
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getResponsiveMedia(media)
const aspectRatio = responsiveMedia?.aspectRatio || {}
const { height: gradientAreaHeight } = useElementSize(gradientAreaRef)
const { height: textAreaHeight } = useElementSize(textAreaRef)
const { top: gradientAreaTop } = useElementBounding(gradientAreaRef)
const { top: textAreaTop } = useElementBounding(textAreaRef)

const headerHeight = computed(() => (header.transparent ? header.height.promoBar : header.height.full) || 0)
const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const hasComponentPercentHeight = componentPercentHeight !== undefined
const usesPercentHeightSmMd = hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
const usesPercentHeightLg = hasPercentHeightAtBreakpoint(componentPercentHeight?.large)
const anchorSmall = componentPercentHeight?.anchorSmall
const anchorLarge = componentPercentHeight?.anchorLarge

const percentHeightStyle = computed(() =>
  getPercentHeightCssVars(componentPercentHeight, percentHeightHeaderOffsetPx.value),
)

const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)
const imageProps = { class: 'h-full' }

const cinemaModeMaxH = Object.entries(cinemaMode || {}).reduce((acc, [key, value]) => {
  acc[key as keyof Responsive] = value ? '50vh' : 'auto'
  return acc
}, {} as Responsive)

const contentPane = Object.keys(aspectRatio).reduce((acc, cur) => {
  acc[cur] = `calc(1 / ${aspectRatio[cur]} * 100%)`
  return acc
}, {})

const parallaxPaddingBottom = Object.fromEntries(
  Object.entries(contentPane).filter(([bp]) =>
    bp === 'lg' ? !usesPercentHeightLg : !usesPercentHeightSmMd,
  ),
)

const parallaxOffset = parallax?.config.innerOffset
const parallaxLayer = useParallaxTextLayer(parallax?.config.textLayer)
const parallaxRef = useTemplateRef('parallaxRef')
const { config: parallaxConfig, translate: parallaxTranslate } = getOffsetParallaxConfig(parallaxOffset, {
  0: 0,
  1: 50,
  2: 70,
  3: 85,
  4: 100,
  5: 125,
})

if (parallaxConfig) {
  useParallaxController(parallaxRef, parallaxConfig)
}

/**
 * @deprecated Backwards compatibility for old alignment values
 */
const contentAlignmentLargeOld: Partial<Record<BannerImageAlignmentLarge, string>> = {
  'left-center': 'md:justify-center',
  'left-right': 'md:justify-end',
  'center-left': 'md:text-center',
  'center-center': 'md:text-center md:justify-center',
  'center-right': 'md:text-center md:justify-end',
}

const contentAlignmentLarge: Partial<Record<BannerImageAlignmentLarge, string>> = {
  ...contentAlignmentLargeOld,
  'bottom-center': 'md:items-end md:justify-center',
  'bottom-left': 'md:items-end md:justify-start',
  'bottom-right': 'md:items-end md:justify-end',
  'middle-center': 'md:center',
  'middle-left': 'md:items-center md:justify-start',
  'middle-right': 'md:items-center md:justify-end',
  'top-center': 'md:items-start md:justify-center',
  'top-left': 'md:items-start md:justify-start',
  'top-right': 'md:items-start md:justify-end',
  'below': ''
}

const contentAlignmentSmall: Partial<Record<BannerImageAlignmentSmall, string>> = {
  'center': '<md:items-center <md:text-center',
  'overlay-top': '<md:items-start',
  'overlay-middle': '<md:items-center',
  'overlay-bottom': '<md:items-end',
  'below': '',
}

const textsAlignmentSmall: Record<TextAlignment, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
}

const textsAlignmentLarge: Record<TextAlignment, string> = {
  left: alignmentLarge !== 'below' ? 'md:text-left' : 'md:justify-start md:text-left',
  center: alignmentLarge !== 'below' ? 'md:text-center' : 'md:justify-center md:text-center',
  right: alignmentLarge !== 'below' ? 'md:text-right' : 'md:justify-end md:text-right'
}

const ctaAlignmentLarge: Partial<Record<BannerImageAlignmentLarge | TextAlignment, string>> = {
  'center-left': 'md:text-center md:justify-center',
  'center-center': 'md:text-center md:justify-center',
  'center-right': 'md:text-center md:justify-center',
  'left': 'md:text-left md:justify-start',
  'center': 'md:text-center md:justify-center',
  'right': 'md:text-right md:justify-end'
}

const ctaAlignmentSmall: Record<TextAlignment, string> = {
  left: '<md:justify-start <md:items-start',
  center: '<md:justify-center <md:items-center <md:text-center',
  right: '<md:justify-end <md:items-end'
}

const gradientPositionSmall: Record<BannerImageAlignmentSmall, 'top' | 'bottom' | 'center'> = {
  'left': 'bottom',
  'center': 'bottom',
  'overlay-top': 'top',
  'overlay-middle': 'center',
  'overlay-bottom': 'bottom',
  'below': 'bottom',
}

const gradientPositionLarge: Record<BannerImageAlignmentLarge, 'top' | 'bottom' | 'center'> = {
  'left-left': 'center',
  'left-center': 'center',
  'left-right': 'center',
  'center-left': 'center',
  'center-center': 'center',
  'center-right': 'center',
  'bottom-center': 'bottom',
  'bottom-left': 'bottom',
  'bottom-right': 'bottom',
  'middle-center': 'center',
  'middle-left': 'center',
  'middle-right': 'center',
  'top-center': 'top',
  'top-left': 'top',
  'top-right': 'top',
  'below': 'bottom',
}

const gradientAlignmentLarge: Record<BannerImageAlignmentLarge, 'left' | 'center' | 'right'> = {
  'left-left': 'left',
  'left-center': 'center',
  'left-right': 'right',
  'center-left': 'left',
  'center-center': 'center',
  'center-right': 'right',
  'bottom-center': 'center',
  'bottom-left': 'left',
  'bottom-right': 'right',
  'middle-center': 'center',
  'middle-left': 'left',
  'middle-right': 'right',
  'top-center': 'center',
  'top-left': 'left',
  'top-right': 'right',
  'below': 'center',
}

const gradientPosition: Responsive<'top' | 'bottom' | 'center'> = {
  sm: gradientPositionSmall[alignmentSmall],
  md: gradientPositionLarge[alignmentLarge],
}

const gradientAlignment: Responsive<'left' | 'center' | 'right'> = {
  sm: textAlignmentSmall,
  md: gradientAlignmentLarge[alignmentLarge],
}

const getTextAreaOnlyExtent = (
  pos?: 'top' | 'bottom' | 'center',
) => {
  const fallbackExtent = pos === 'center' ? '67%' : '33%'
  if (!textAreaHeight.value)
    return fallbackExtent

  const extent = pos === 'bottom'
    ? gradientAreaHeight.value && textAreaTop.value && gradientAreaTop.value
      ? Math.max(textAreaHeight.value, gradientAreaHeight.value - (textAreaTop.value - gradientAreaTop.value))
      : textAreaHeight.value
    : pos === 'center'
      ? gradientAreaHeight.value
        ? (gradientAreaHeight.value + textAreaHeight.value) / 2
        : undefined
      : textAreaHeight.value

  if (!extent || extent <= 0)
    return fallbackExtent

  return `${Math.ceil(extent)}px`
}

const textAreaOnlyExtent = computed<Responsive<string> | undefined>(() => {
  if (!isTextAreaOnlyGradient)
    return undefined

  const smallExtent = getTextAreaOnlyExtent(gradientPosition.sm)
  const mediumExtent = getTextAreaOnlyExtent(gradientPosition.md)
  const resolvedExtent = smallExtent || mediumExtent

  if (!resolvedExtent)
    return undefined

  return {
    sm: resolvedExtent,
    ...(mediumExtent && { md: mediumExtent }),
    lg: mediumExtent || resolvedExtent,
  }
})

const { hasCaption, captionProps } = getCaptionConfig(content)

const contentSize = {
  xs: 'md:max-w-1/3',
  sm: 'md:max-w-1/2',
  md: 'md:max-w-2/3',
  lg: 'md:max-w-11/12'
}

const cols = targets.map((target) => target.style?.sm === 'text-link' ? 'max-content' : 'minmax(0,1fr)').join(' ')

const titleTag = computed(() => titleH1
  ? 'h1'
  : isPartOfCollection
    ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
    : firstElement ? 'h1' : 'h2')

const hasHotZones = Object.values(media || []).some((m) => m.enableImageMap)

const roundedCornersClasses = getRoundedCorners(content, componentCorners)
const overlayRoundedCornerClasses = getRoundedCorners(
  { componentSidePaddings: { sm: false, lg: true } } as Content,
  componentCorners
)

const containerWrapperExtendedClasses = computed(() => [
  ((isPartOfCollection || !!responsiveMedia.video) && alignmentLarge.startsWith('bottom-')) && backdrop && brandClasses!.containerWrapperEnlargedBottom,
  (firstElement && header.transparent && alignmentSmall === 'overlay-top') && getValueForBreakpoint('sm', brandClasses!.containerWrapperEnlargedTop),
  (firstElement && header.transparent) && getValueForBreakpoint('md', brandClasses!.containerWrapperEnlargedTop)
])

const captionOffsetClasses = [
  firstElement && header.transparent && captionProps.captionPosition?.startsWith('top') && brandClasses!.captionOffsetTop,
  (isPartOfCollection || !!responsiveMedia.video) && captionProps.captionPosition?.startsWith('bottom') && brandClasses!.captionOffsetBottom,
]

const captionClasses = computed(() => alignWithOverlay
  ? [brandClasses!.captionOverlayPadding ?? '', containerWrapperExtendedClasses.value]
  : captionOffsetClasses)

useAutoplay(videoRef)

watch(() => paused, (paused) => paused ? videoRef.value?.pause() : videoRef.value?.play())

const blurClass = backdropBlur && (backdropBlur === 'low' ? 'backdrop-blur-xs' : 'backdrop-blur-md')
</script>
